import { useState, type JSX } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import type { PendingInvite } from '@profiler/shared'
import { SectionHeader } from '../../components/Page'
import { EM_DASH, formatDate } from '../../format'

/**
 * Invitations nobody has used yet.
 *
 * Once somebody signs in they become a row in the list above and leave this
 * one. Cancelling is for the address that was mistyped or the person who will
 * not be joining after all; somebody who has joined is disabled instead.
 * Resending is for the email that went to junk, or the one that failed.
 */
export function PendingInvites({
  invites,
  busy,
  error,
  onCancel,
  resending,
  onResend,
}: Readonly<{
  invites: readonly PendingInvite[]
  busy: boolean
  error: unknown
  onCancel: (email: string) => Promise<unknown>
  /** The address whose email is being sent again, if any. */
  resending: string | null
  onResend: (email: string) => void
}>): JSX.Element | null {
  const [confirming, setConfirming] = useState<string | null>(null)
  const [shown, setShown] = useState<string | null>(null)

  if (invites.length === 0) return null

  const ask = (email: string): void => {
    setShown(email)
    setConfirming(email)
  }

  return (
    <Card>
      <CardContent sx={{ pb: 0 }}>
        <SectionHeader
          title="Invited, not signed in yet"
          description="They join Quarry the first time they sign in with the invited address."
        />
      </CardContent>
      <TableContainer>
        <Table size="small" sx={{ minWidth: 680 }}>
          <TableHead>
            <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
              <TableCell>Address</TableCell>
              <TableCell>Invited by</TableCell>
              <TableCell>Invited</TableCell>
              <TableCell align="right" aria-label="Actions" />
            </TableRow>
          </TableHead>
          <TableBody>
            {invites.map((invite) => (
              <TableRow key={invite.email} hover>
                <TableCell>{invite.email}</TableCell>
                <TableCell>{invite.invitedBy || EM_DASH}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(invite.invitedAt)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Button
                    size="small"
                    disabled={resending !== null}
                    onClick={() => onResend(invite.email)}
                    sx={{ mr: 1 }}
                  >
                    {resending === invite.email ? 'Sending…' : 'Resend email'}
                  </Button>
                  <Button size="small" color="error" onClick={() => ask(invite.email)}>
                    Cancel invitation
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={confirming !== null}
        onClose={busy ? undefined : () => setConfirming(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cancel the invitation for {shown}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            They will not be able to join unless somebody invites them again. Nothing else changes.
          </DialogContentText>
          {error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error instanceof Error ? error.message : String(error)}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)} disabled={busy}>
            Keep it
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={busy}
            onClick={() => {
              if (!confirming) return
              void onCancel(confirming).then(
                () => setConfirming(null),
                () => undefined,
              )
            }}
          >
            Cancel invitation
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}
